import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import client, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import DataTransfer from "./settings/DataTransfer";

function Settings({ brand, reloadBrand, isSuper }) {
  const { prefs, reloadPrefs, wallpaper, reloadWallpaper } = useAuth();
  const [pin, setPin] = useState({ current: "", next: "", confirm: "" });
  const [brandForm, setBrandForm] = useState({ title: "", subtitle: "" });
  const [signoutMin, setSignoutMin] = useState(5);
  const [wpForm, setWpForm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setBrandForm({ title: brand?.title || "", subtitle: brand?.subtitle || "" }); }, [brand]);
  useEffect(() => { setSignoutMin(prefs?.auto_signout_min || 5); }, [prefs]);
  useEffect(() => { setWpForm(wallpaper?.url || ""); }, [wallpaper]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const changePin = () => {
    if (pin.next !== pin.confirm) { toast.error("New PINs don't match"); return; }
    if (pin.next.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
    run(async () => {
      await client.post("/auth/change-pin", { current_pin: pin.current, new_pin: pin.next });
      setPin({ current: "", next: "", confirm: "" });
    }, "Owner PIN updated");
  };
  const saveBrand = () => run(async () => {
    await client.put("/brand", { title: brandForm.title, subtitle: brandForm.subtitle });
    await reloadBrand();
  }, "Name updated");
  const saveSignout = () => run(async () => {
    await client.put("/prefs", { auto_signout_min: signoutMin });
    await reloadPrefs();
  }, `Auto sign-out set to ${signoutMin} minute${signoutMin === 1 ? "" : "s"}`);
  const saveWallpaper = () => run(async () => {
    await client.put("/wallpaper", { url: wpForm });
    await reloadWallpaper();
  }, wpForm ? "Wallpaper updated" : "Wallpaper removed");

  const inputCls = "w-full px-3 py-3 bg-panel-soft text-ivory border border-line rounded-lg text-[15px] focus:outline-none";
  const card = "bg-panel border border-line rounded-xl p-4.5";
  const pinField = (k, placeholder, testid, extra = " mb-2.5") => (
    <input
      type="password"
      inputMode="numeric"
      value={pin[k]}
      onChange={(e) => setPin({ ...pin, [k]: e.target.value.replace(/\D/g, "") })}
      placeholder={placeholder}
      data-testid={testid}
      maxLength={12}
      className={inputCls + extra}
    />
  );

  return (
    <div className="space-y-5">
      <div className={card}>
        <div className="caps text-muted-jewel mb-2.5">Display name</div>
        <p className="text-muted-jewel text-[13px] mb-2.5">What appears at the top of the app. Leave subtitle blank to hide it.</p>
        <input value={brandForm.title} onChange={(e) => setBrandForm({ ...brandForm, title: e.target.value })} placeholder="Register" data-testid="settings-brand-title" maxLength={80} className={inputCls + " mb-2.5"} />
        <input value={brandForm.subtitle} onChange={(e) => setBrandForm({ ...brandForm, subtitle: e.target.value })} placeholder="Subtitle (optional)" data-testid="settings-brand-subtitle" maxLength={80} className={inputCls} />
        <button onClick={saveBrand} disabled={busy} data-testid="settings-save-brand" className="seal-primary w-full mt-3 rounded-lg py-3 text-[15px] font-semibold disabled:opacity-40">Save name</button>
      </div>

      <div className={card}>
        <div className="caps text-muted-jewel mb-2.5">Wallpaper</div>
        <p className="text-muted-jewel text-[13px] mb-2.5">Paste an image URL. Shows behind the shop screen. Leave blank to remove.</p>
        <input value={wpForm} onChange={(e) => setWpForm(e.target.value)} placeholder="https://…" data-testid="settings-wallpaper-url" maxLength={2000} className={inputCls} />
        {wpForm && (
          <img src={wpForm} alt="" data-testid="settings-wallpaper-preview" className="mt-3 w-full h-24 object-cover rounded-md opacity-70" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <button onClick={saveWallpaper} disabled={busy} data-testid="settings-save-wallpaper" className="seal-outline w-full mt-3 rounded-lg py-3 text-[15px] font-semibold disabled:opacity-40">Save wallpaper</button>
      </div>

      <div className={card}>
        <div className="caps text-muted-jewel mb-2.5">Auto sign-out</div>
        <p className="text-muted-jewel text-[13px] mb-2.5">Log the owner out of this panel after this many minutes of no activity. The shop screen stays open.</p>
        <div className="flex items-center gap-3">
          <input type="number" min={1} max={120} value={signoutMin} onChange={(e) => setSignoutMin(Math.max(1, Math.min(120, parseInt(e.target.value || "1", 10))))} data-testid="settings-signout-min" className={inputCls + " max-w-[100px]"} />
          <span className="text-muted-jewel text-sm">minutes</span>
        </div>
        <button onClick={saveSignout} disabled={busy} data-testid="settings-save-signout" className="seal-outline w-full mt-3 rounded-lg py-3 text-[15px] font-semibold disabled:opacity-40">Save</button>
      </div>

      <div className={card}>
        <div className="caps text-muted-jewel mb-2.5">Change owner PIN</div>
        <p className="text-muted-jewel text-[13px] mb-2.5">The same PIN opens and closes the shop screen.</p>
        {pinField("current", "Current PIN", "settings-current-pin")}
        {pinField("next", "New PIN", "settings-new-pin")}
        {pinField("confirm", "Confirm new PIN", "settings-confirm-pin", "")}
        <button onClick={changePin} disabled={busy} data-testid="settings-save-pin" className="seal-primary w-full mt-3 rounded-lg py-3 text-[15px] font-semibold disabled:opacity-40">Save PIN</button>
      </div>

      {isSuper && <DataTransfer />}
    </div>
  );
}

export default Settings;
